import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Wallet, TrendingUp, TrendingDown, Calendar, HelpCircle, Loader2, HandCoins,
  Clock, AlertTriangle, Smartphone, Database, Monitor, Users, Receipt, CreditCard,
  CheckCircle2, CircleDollarSign,
} from 'lucide-react'
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts'
import { api, formatMoney } from '@/lib/api'

const COBRANCA_LABEL: any = {
  dinheiro: 'Dinheiro', boleto: 'Boleto', prazo: 'A prazo', cheque: 'Cheque',
  pix: 'PIX', cartao_credito: 'Cartão', '—': 'Não informado',
}
const PERIODOS = [
  ['mes', 'Mês atual'], ['mes_anterior', 'Mês anterior'],
  ['ult30', 'Últimos 30 dias'], ['ult90', 'Últimos 90 dias'], ['personalizado', 'Personalizado'],
] as const

function diaCurto(iso: string) { const [, m, d] = iso.split('-'); return `${d}/${m}` }

function Ajuda({ children }: { children: React.ReactNode }) {
  return (
    <span className="group relative inline-flex">
      <HelpCircle size={13} className="text-ink-400 cursor-help" />
      <span className="invisible group-hover:visible absolute left-1/2 -translate-x-1/2 top-5 z-20 w-64
        bg-ink-800 text-white text-[11px] leading-snug rounded-lg px-3 py-2 shadow-lg font-normal">
        {children}
      </span>
    </span>
  )
}

function Secao({ titulo, icon, ajuda, children, className = '' }: any) {
  return (
    <div className={`bg-white rounded-2xl border border-ink-200 shadow-sm p-4 ${className}`}>
      <div className="flex items-center gap-2 mb-3">
        {icon}<h3 className="font-semibold text-ink-800 text-sm">{titulo}</h3>
        {ajuda && <Ajuda>{ajuda}</Ajuda>}
      </div>
      {children}
    </div>
  )
}

const fmtPct = (v: any) => v == null ? '—' : `${v}%`

// Barra recebido vs aberto
function BarraRecAberto({ recebido, aberto }: any) {
  const t = (recebido + aberto) || 1
  return (
    <div className="flex h-2 rounded-full overflow-hidden bg-ink-100" title={`Recebido ${formatMoney(recebido)} · Aberto ${formatMoney(aberto)}`}>
      <div className="bg-emerald-500" style={{ width: `${recebido / t * 100}%` }} />
      <div className="bg-amber-400" style={{ width: `${aberto / t * 100}%` }} />
    </div>
  )
}

// ============================================================================
export default function VendaRecebimentosPage() {
  const [periodo, setPeriodo] = useState('mes')
  const [de, setDe] = useState('')
  const [ate, setAte] = useState('')
  const params: any = periodo === 'personalizado' && de && ate ? { de, ate } : { periodo }

  const { data, isLoading } = useQuery<any>({
    queryKey: ['venda-recebimentos', params],
    queryFn: async () => (await api.get('/financeiro/venda-recebimentos', { params })).data,
  })

  return (
    <div className="p-4 lg:p-6 space-y-4 bg-ink-50/40 min-h-full">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-ink-800">
            <CircleDollarSign className="text-emerald-600" size={26} /> Venda por Recebimentos
          </h1>
          <p className="text-xs text-ink-500 mt-0.5">
            Vendas e seus títulos a receber, <b>por competência</b> (mês da venda) — não pela data de pagamento.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Calendar size={16} className="text-ink-400" />
          <select value={periodo} onChange={e => setPeriodo(e.target.value)}
            className="text-sm border border-ink-300 rounded-lg px-3 py-1.5 bg-white">
            {PERIODOS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          {periodo === 'personalizado' && (
            <>
              <input type="date" value={de} onChange={e => setDe(e.target.value)} className="text-sm border border-ink-300 rounded-lg px-2 py-1.5 bg-white" />
              <span className="text-ink-400 text-sm">até</span>
              <input type="date" value={ate} onChange={e => setAte(e.target.value)} className="text-sm border border-ink-300 rounded-lg px-2 py-1.5 bg-white" />
            </>
          )}
        </div>
      </div>

      {isLoading && <div className="grid place-items-center py-20 text-ink-400"><Loader2 className="animate-spin" size={28} /></div>}

      {data && (
        <>
          {/* Banner de competência */}
          <div className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl px-3 py-2 flex items-center gap-2">
            <HelpCircle size={14} />
            <span><b>Por competência:</b> um título de abril que será pago em maio conta em <b>abril</b> (mês da venda). Período: <b>{data.periodo.label}</b> ({diaCurto(data.periodo.de)} a {diaCurto(data.periodo.ate)}).</span>
          </div>

          {/* TRIO PRINCIPAL */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CardGrande titulo="Vendas no período" valor={data.cards.vendas} cor="violet" icon={<Receipt size={20} />}
              badge={data.cards.variacao_pct} sub={`${data.cards.titulos} títulos · ${data.cards.clientes} clientes`}
              explica="Soma dos títulos a receber pela competência (mês da venda). Inclui o que já foi pago e o que ainda está em aberto. A seta compara com o período anterior de mesmo tamanho." />
            <CardGrande titulo="Recebido" valor={data.cards.recebido} cor="emerald" icon={<HandCoins size={20} />}
              chip={`${data.cards.taxa_recebimento}% das vendas`} sub="já entrou no caixa"
              explica="Quanto dessas vendas já foi efetivamente pago (valor pago dos títulos), mesmo que o pagamento tenha caído num mês posterior." />
            <CardGrande titulo="Em aberto" valor={data.cards.aberto} cor="amber" icon={<Clock size={20} />}
              chip={`${data.cards.pct_aberto}% das vendas`} sub="saldo ainda a receber"
              explica="Saldo a receber (valor − pago) dos títulos ainda não quitados. Vendas = Recebido + Em aberto." />
          </div>

          {/* Reconciliação + aging */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Secao titulo="Como fecha a conta" icon={<CheckCircle2 size={16} className="text-violet-600" />} className="lg:col-span-2"
              ajuda="O total vendido se divide exatamente entre o que já foi recebido e o que continua em aberto.">
              <div className="flex items-center gap-3 text-sm flex-wrap">
                <Bloco label="Vendas" valor={data.cards.vendas} cor="violet" />
                <span className="text-ink-400 font-bold">=</span>
                <Bloco label="Recebido" valor={data.cards.recebido} cor="emerald" />
                <span className="text-ink-400 font-bold">+</span>
                <Bloco label="Em aberto" valor={data.cards.aberto} cor="amber" />
              </div>
              <div className="mt-3"><BarraRecAberto recebido={data.cards.recebido} aberto={data.cards.aberto} /></div>
              <div className="flex gap-4 mt-2 text-[11px] text-ink-500">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Recebido</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" /> Em aberto</span>
              </div>
            </Secao>
            <Secao titulo="Situação do que está em aberto" icon={<AlertTriangle size={16} className="text-rose-600" />}
              ajuda="Do saldo em aberto, quanto já venceu (inadimplência) e quanto ainda está dentro do prazo.">
              <div className="grid grid-cols-2 gap-3">
                <Bloco label="Vencido" valor={data.cards.vencido} cor="rose" sub={`${data.cards.pct_inadimplencia}% das vendas`} />
                <Bloco label="A vencer" valor={data.cards.a_vencer} cor="blue" />
              </div>
            </Secao>
          </div>

          {/* EXPLICAÇÃO: por que difere da Venda Geral */}
          <Secao titulo="Por que difere da tela “Venda Geral”?" icon={<HelpCircle size={16} className="text-blue-600" />}
            ajuda="Venda Geral conta PEDIDOS. Este painel conta TÍTULOS A RECEBER (financeiro). Nem todo pedido tem título.">
            <div className="flex items-center gap-3 text-sm flex-wrap mb-3">
              <Bloco label="Pedidos (Venda Geral)" valor={data.cards.vendas_pedidos} cor="blue" />
              <span className="text-ink-400 font-bold">−</span>
              <Bloco label="Títulos a receber (aqui)" valor={data.cards.vendas} cor="violet" />
              <span className="text-ink-400 font-bold">=</span>
              <Bloco label="Diferença" valor={data.cards.diferenca_pedidos} cor="amber" />
            </div>
            <ul className="text-xs text-ink-600 space-y-1 list-disc pl-5">
              <li><b>Venda Geral</b> soma os <b>pedidos</b> (tudo que foi vendido, mesmo sem faturar).</li>
              <li><b>Este painel</b> soma os <b>títulos a receber</b> gerados — é a visão do financeiro.</li>
              <li>A diferença = pedidos que <b>ainda não viraram título</b> (não faturados) + vendas <b>migradas do WinThor</b>, cujos títulos em aberto entraram à parte dos pedidos.</li>
              <li>Aqui, <b>Vendas = Recebido + Em aberto</b> sempre fecha, porque tudo parte do mesmo título.</li>
            </ul>
          </Secao>

          {/* POR ORIGEM */}
          <Secao titulo="Títulos por origem" icon={<Database size={16} className="text-blue-600" />}
            ajuda="De onde veio cada venda: migrada do WinThor, lançada pelo app no celular, ou digitada aqui no Velore.">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <CardOrigem titulo="Migrada do WinThor" icon={<Database size={18} />} cor="slate" d={data.por_origem.winthor} />
              <CardOrigem titulo="Veio do Celular (app)" icon={<Smartphone size={18} />} cor="blue" d={data.por_origem.celular} />
              <CardOrigem titulo="Digitada no Velore" icon={<Monitor size={18} />} cor="violet" d={data.por_origem.velore} />
            </div>
          </Secao>

          {/* SÉRIE */}
          <Secao titulo="Vendas × Recebido por dia (competência)" icon={<TrendingUp size={16} className="text-violet-600" />}
            ajuda="Linha do tempo: quanto foi vendido e quanto foi recebido em cada dia do período.">
            {data.serie.length === 0 ? <Vazio /> : (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={data.serie}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="data" tickFormatter={diaCurto} fontSize={11} />
                  <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} fontSize={11} />
                  <Tooltip formatter={(v: any) => formatMoney(v)} labelFormatter={diaCurto} />
                  <Legend />
                  <Area type="monotone" dataKey="vendas" name="Vendas" stroke="#8b5cf6" fill="#ede9fe" strokeWidth={2} />
                  <Area type="monotone" dataKey="recebido" name="Recebido" stroke="#10b981" fill="#d1fae5" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Secao>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* POR COBRANÇA */}
            <Secao titulo="Por cobrança (forma de pagamento)" icon={<CreditCard size={16} className="text-emerald-600" />}
              ajuda="Para cada forma de pagamento: total vendido, quanto já foi recebido e quanto ficou em aberto.">
              <TabelaMetrica linhas={data.por_cobranca} chave="forma" label={(f: string) => COBRANCA_LABEL[f] || f} />
            </Secao>

            {/* POR PRAZO */}
            <Secao titulo="Por prazo (condição de pagamento)" icon={<Clock size={16} className="text-blue-600" />}
              ajuda="Agrupado pela condição de pagamento da venda (à vista, 30/60/90, etc.). Mostra venda, recebido e aberto de cada prazo.">
              <TabelaMetrica linhas={data.por_prazo} chave="prazo" label={(p: string) => p} />
            </Secao>
          </div>

          {/* POR VENDEDOR */}
          <Secao titulo="Por vendedor" icon={<Users size={16} className="text-violet-600" />}
            ajuda="Vendas, recebido e em aberto de cada vendedor — útil pra cobrança e acompanhamento da carteira dele.">
            {data.por_vendedor.length === 0 ? <Vazio /> : (
              <table className="w-full text-sm">
                <thead><tr className="text-ink-500 text-xs border-b">
                  <th className="text-left py-1.5">Vendedor</th><th className="text-right">Títulos</th>
                  <th className="text-right">Vendas</th><th className="text-right">Recebido</th>
                  <th className="text-right">Em aberto</th><th className="w-28">Recebido/Aberto</th>
                </tr></thead>
                <tbody>
                  {data.por_vendedor.map((v: any, i: number) => (
                    <tr key={i} className="border-b last:border-0 hover:bg-ink-50">
                      <td className="py-1.5 font-medium">{v.vendedor}</td>
                      <td className="text-right text-ink-500">{v.titulos}</td>
                      <td className="text-right font-semibold">{formatMoney(v.vendas)}</td>
                      <td className="text-right text-emerald-600">{formatMoney(v.recebido)}</td>
                      <td className="text-right text-amber-600">{formatMoney(v.aberto)}</td>
                      <td className="px-2"><BarraRecAberto recebido={v.recebido} aberto={v.aberto} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Secao>

          {/* INADIMPLENTES */}
          <Secao titulo="Top inadimplentes — saldo vencido" icon={<AlertTriangle size={16} className="text-rose-600" />}
            ajuda="Clientes com títulos vencidos e não pagos (do período por competência). Priorize a cobrança destes.">
            {data.inadimplentes.length === 0 ? <Vazio txt="Nenhum título vencido em aberto 🎉" /> : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                {data.inadimplentes.map((c: any, i: number) => (
                  <div key={i} className="flex justify-between border-b last:border-0 py-1">
                    <span className="truncate">{c.cliente}</span>
                    <span className="font-semibold text-rose-600 whitespace-nowrap ml-2">{formatMoney(c.devido)}</span>
                  </div>
                ))}
              </div>
            )}
          </Secao>
        </>
      )}
    </div>
  )
}

// ============================================================================
function CardGrande({ titulo, valor, sub, cor, icon, badge, chip, explica }: any) {
  const cores: any = {
    violet: 'from-violet-500 to-violet-600', emerald: 'from-emerald-500 to-emerald-600',
    amber: 'from-amber-500 to-amber-600',
  }
  return (
    <div className="bg-white rounded-2xl border border-ink-200 shadow-sm p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${cores[cor]} text-white grid place-items-center`}>{icon}</div>
          <span className="text-sm font-medium text-ink-600">{titulo}</span>
          <Ajuda>{explica}</Ajuda>
        </div>
        {badge != null && (
          <span className={`text-xs font-semibold flex items-center gap-0.5 px-1.5 py-0.5 rounded ${badge >= 0 ? 'text-emerald-700 bg-emerald-50' : 'text-rose-700 bg-rose-50'}`}>
            {badge >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}{badge >= 0 ? '+' : ''}{badge}%
          </span>
        )}
      </div>
      <div className="mt-2 text-2xl font-bold text-ink-800">{formatMoney(valor)}</div>
      <div className="flex items-center gap-2 mt-0.5">
        {chip && <span className="text-[11px] font-semibold text-ink-600 bg-ink-100 rounded px-1.5 py-0.5">{chip}</span>}
        {sub && <span className="text-xs text-ink-500">{sub}</span>}
      </div>
    </div>
  )
}

function Bloco({ label, valor, cor, sub }: any) {
  const cores: any = {
    violet: 'text-violet-700 bg-violet-50', emerald: 'text-emerald-700 bg-emerald-50',
    amber: 'text-amber-700 bg-amber-50', rose: 'text-rose-700 bg-rose-50', blue: 'text-blue-700 bg-blue-50',
  }
  return (
    <div className={`rounded-xl px-3 py-2 ${cores[cor]}`}>
      <div className="text-[11px] uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-lg font-bold">{formatMoney(valor)}</div>
      {sub && <div className="text-[10px] opacity-70">{sub}</div>}
    </div>
  )
}

function CardOrigem({ titulo, icon, cor, d }: any) {
  const cores: any = { slate: 'text-slate-600 bg-slate-100', blue: 'text-blue-600 bg-blue-100', violet: 'text-violet-600 bg-violet-100' }
  return (
    <div className="rounded-xl border border-ink-200 p-3">
      <div className="flex items-center gap-2 mb-1">
        <div className={`w-7 h-7 rounded-lg grid place-items-center ${cores[cor]}`}>{icon}</div>
        <span className="text-sm font-medium text-ink-700">{titulo}</span>
      </div>
      <div className="text-xl font-bold text-ink-800">{formatMoney(d.vendas)}</div>
      <div className="text-[11px] text-ink-500">{d.titulos} títulos</div>
      <div className="mt-2"><BarraRecAberto recebido={d.recebido} aberto={d.aberto} /></div>
      <div className="flex justify-between text-[11px] mt-1">
        <span className="text-emerald-600">Receb. {formatMoney(d.recebido)}</span>
        <span className="text-amber-600">Aberto {formatMoney(d.aberto)}</span>
      </div>
    </div>
  )
}

function TabelaMetrica({ linhas, chave, label }: any) {
  if (!linhas || linhas.length === 0) return <Vazio />
  return (
    <table className="w-full text-sm">
      <thead><tr className="text-ink-500 text-xs border-b">
        <th className="text-left py-1.5">{chave === 'forma' ? 'Forma' : 'Prazo'}</th>
        <th className="text-right">Vendas</th><th className="text-right">Recebido</th><th className="text-right">Em aberto</th>
      </tr></thead>
      <tbody>
        {linhas.map((l: any, i: number) => (
          <tr key={i} className="border-b last:border-0 hover:bg-ink-50">
            <td className="py-1.5 font-medium">{label(l[chave])}</td>
            <td className="text-right font-semibold">{formatMoney(l.vendas)}</td>
            <td className="text-right text-emerald-600">{formatMoney(l.recebido)}</td>
            <td className="text-right text-amber-600">{formatMoney(l.aberto)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function Vazio({ txt = 'Sem dados no período' }: { txt?: string }) {
  return <div className="text-center text-ink-400 text-sm py-8">{txt}</div>
}
